<script setup lang="ts">
/**
 * Singapura — AR Verification of Data. 원본 서식(AR-verification of data.docx)을 그대로 재현.
 *   섹션·라벨·원문 텍스트는 원본대로, 값칸 = CddVarField. 반복부 = VerifyLoopBlock, 체크박스 = doc-toggle(☑/☐).
 *   저장 값은 normalize 가 템플릿 [[변수]]·loop 배열로 변환한다.
 */
import { computed } from 'vue';
import CddVarField from '../../aura/agm/cdd/CddVarField.vue';
import { useAgmFill } from '../../aura/agm/cdd/useCddFill';
import VerifyLoopBlock from './VerifyLoopBlock.vue';
import { V_LOOPS, V_YESNO, V_EMP_BANDS } from './verifyLoops';
import '../../aura/agm/docs/agmDoc.css';

const fill = useAgmFill();

function yn(k: string): string {
  const v = String(fill.values[k] ?? '').trim().toLowerCase();
  return v === 'yes' || v === 'true' || v === '1' ? 'yes' : v === 'no' || v === 'false' || v === '0' ? 'no' : '';
}
function setYn(k: string, val: 'yes' | 'no') { fill.setValue(k, yn(k) === val ? '' : val); }
function band(): string { return String(fill.values.employee_count_range ?? '').trim(); }
function setBand(v: string) { fill.setValue('employee_count_range', band() === v ? '' : v); }

// ── 주식자본(통화별) 반복 표 ──
const scCount = computed(() => {
  const n = parseInt(String(fill.values.share_capital_count ?? '').trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
});
const scRows = computed(() => Array.from({ length: scCount.value }, (_, i) => i + 1));
const SC_FIELDS = ['currency', 'issued_ordinary', 'issued_preference', 'paidup_ordinary', 'paidup_preference', 'shares_ordinary', 'shares_preference'];
function addSc() { fill.setValue('share_capital_count', String(scCount.value + 1)); }
function removeSc(idx: number) {
  const n = scCount.value;
  for (let k = idx; k < n; k++) for (const f of SC_FIELDS) fill.setValue(`share_capital_${k}_${f}`, String(fill.values[`share_capital_${k + 1}_${f}`] ?? ''));
  for (const f of SC_FIELDS) fill.setValue(`share_capital_${n}_${f}`, '');
  fill.setValue('share_capital_count', String(Math.max(0, n - 1)));
}

// ── 담보(charges) 컬럼형 표 ──
// "Nil" indebtedness — 체크하면 표 4칸이 모두 NIL 인 1행으로 생성(고객 요청). 행 입력은 숨긴다.
const chargesNil = computed(() => yn('charges_nil') === 'yes');
function toggleChargesNil() { fill.setValue('charges_nil', chargesNil.value ? '' : 'yes'); }
const chargeCount = computed(() => {
  const n = parseInt(String(fill.values.charge_count ?? '').trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
});
const chargeRows = computed(() => Array.from({ length: chargeCount.value }, (_, i) => i + 1));
function addCharge() { fill.setValue('charge_count', String(chargeCount.value + 1)); }
function removeCharge(idx: number) {
  const n = chargeCount.value;
  const fs = ['date', 'no', 'amount', 'chargee'];
  for (let k = idx; k < n; k++) for (const f of fs) fill.setValue(`charge_${k}_${f}`, String(fill.values[`charge_${k + 1}_${f}`] ?? ''));
  for (const f of fs) fill.setValue(`charge_${n}_${f}`, '');
  fill.setValue('charge_count', String(Math.max(0, n - 1)));
}
</script>

<template>
  <div class="agm-doc vdoc">
    <!-- Header block -->
    <table class="cdd-tbl vhdr">
      <tbody>
        <tr><td class="cdd-lcell">To</td><td>: <CddVarField field-key="to_director_name" label="Name of director" /></td></tr>
        <tr><td class="cdd-lcell">Name of Company</td><td>: <CddVarField field-key="company_name" label="Company Name" /></td></tr>
        <tr><td class="cdd-lcell">Unique Entity Number</td><td>: <CddVarField field-key="company_uen" label="UEN" /></td></tr>
        <tr><td class="cdd-lcell">For the financial year ended</td><td>: <CddVarField field-key="financial_year_end" label="FYE" /></td></tr>
      </tbody>
    </table>

    <p class="doc-p">Please kindly verify, confirm their correctness and complete where necessary.</p>
    <p class="doc-p">For updating/amendment, please cancel and insert correct particulars as appropriate and then return this copy back to <b>SINGAPURA MANAGEMENT PTE. LTD.</b>:</p>

    <!-- Registered office -->
    <p class="doc-p"><b>Address of Registered Office:</b></p>
    <table class="cdd-tbl" style="width:auto">
      <tbody>
        <tr>
          <td style="min-width:260px;vertical-align:top"><CddVarField field-key="registered_office_address" label="Registered Office Address" /></td>
          <td class="cdd-lcell" style="vertical-align:top">Effective date:<br /><CddVarField field-key="registered_office_effective_date" label="Effective date" /></td>
        </tr>
      </tbody>
    </table>

    <!-- Charges -->
    <p class="doc-p" style="margin-top:12px">Total amount of indebtedness of the Company in respect of all charges which are required to be registered with ACRA: <CddVarField field-key="total_indebtedness" label="Total indebtedness" /></p>
    <div class="sec-title vlb-t">Charges registered with ACRA:<button v-if="!chargesNil" type="button" class="vlb-add" @click="addCharge">+ Add</button>
      <span class="doc-toggle" :class="{ off: !chargesNil }" style="font-weight:600" @click="toggleChargesNil">{{ chargesNil ? '☑' : '☐' }} Nil indebtedness</span>
    </div>
    <table class="cdd-tbl" style="margin-top:6px">
      <thead><tr><th>Date of registration</th><th>Charge No.</th><th>Amount secured</th><th>Name of Chargee</th><th></th></tr></thead>
      <tbody>
        <tr v-if="chargesNil"><td>NIL</td><td>NIL</td><td>NIL</td><td>NIL</td><td></td></tr>
        <tr v-else-if="!chargeCount"><td colspan="5" class="doc-note">— none —</td></tr>
        <tr v-for="i in (chargesNil ? [] : chargeRows)" :key="i">
          <td><CddVarField :field-key="`charge_${i}_date`" label="Date" /></td>
          <td><CddVarField :field-key="`charge_${i}_no`" label="Charge No." /></td>
          <td><CddVarField :field-key="`charge_${i}_amount`" label="Amount" /></td>
          <td><CddVarField :field-key="`charge_${i}_chargee`" label="Chargee" /></td>
          <td><button type="button" class="vlb-rm" @click="removeCharge(i)">×</button></td>
        </tr>
      </tbody>
    </table>

    <!-- Share capital — 통화별 반복 -->
    <div class="sec-title vlb-t" style="margin-top:14px">Summary of share capital and shares:<button type="button" class="vlb-add" @click="addSc">+ Add currency</button></div>
    <p v-if="!scCount" class="doc-note">— none — (click “+ Add currency”)</p>
    <table v-for="i in scRows" :key="i" class="cdd-tbl" style="width:auto;margin-top:8px">
      <tbody>
        <tr><td class="cdd-lcell">Class of shares <button type="button" class="vlb-rm" style="float:right" @click="removeSc(i)">×</button></td><th>Ordinary</th><th>Preference</th></tr>
        <tr><td class="cdd-lcell">Currency</td><td colspan="2"><CddVarField :field-key="`share_capital_${i}_currency`" label="Currency" /></td></tr>
        <tr><td class="cdd-lcell">Issued share capital</td><td><CddVarField :field-key="`share_capital_${i}_issued_ordinary`" label="Ordinary" /></td><td><CddVarField :field-key="`share_capital_${i}_issued_preference`" label="Preference" /></td></tr>
        <tr><td class="cdd-lcell">Paid-up capital</td><td><CddVarField :field-key="`share_capital_${i}_paidup_ordinary`" label="Ordinary" /></td><td><CddVarField :field-key="`share_capital_${i}_paidup_preference`" label="Preference" /></td></tr>
        <tr><td class="cdd-lcell">Number of shares</td><td><CddVarField :field-key="`share_capital_${i}_shares_ordinary`" label="Ordinary" /></td><td><CddVarField :field-key="`share_capital_${i}_shares_preference`" label="Preference" /></td></tr>
      </tbody>
    </table>

    <!-- Principal activity -->
    <div class="sec-title" style="margin-top:14px">Principal activity(ies) in the course of the financial year:</div>
    <table class="cdd-tbl" style="width:auto">
      <tbody>
        <tr><td class="cdd-lcell">Primary</td><td><CddVarField field-key="primary_ssic_code" label="Code" /></td><td><CddVarField field-key="primary_ssic_desc" label="Description" /></td></tr>
        <tr><td class="cdd-lcell">Secondary</td><td><CddVarField field-key="secondary_ssic_code" label="Code" /></td><td><CddVarField field-key="secondary_ssic_desc" label="Description" /></td></tr>
      </tbody>
    </table>

    <!-- Size tests -->
    <div class="sec-title" style="margin-top:14px">Please circle your answer:</div>
    <table class="cdd-tbl">
      <tbody>
        <tr v-for="(q, qi) in V_YESNO.slice(0, 3)" :key="q.k">
          <td class="cdd-lcell" style="width:34px">({{ String.fromCharCode(97 + qi) }})</td>
          <td>
            <template v-if="q.k === 'revenue_exceeds_10m'">Is the Company or Consolidated Group’s revenue (whichever is applicable) exceeding SGD10 million for year ended <CddVarField field-key="financial_year_end" label="FYE" />?</template>
            <template v-else-if="q.k === 'total_assets_exceeds_10m'">Is the Company or Consolidated Group’s total assets (whichever is applicable) exceeding SGD10 million for year ended <CddVarField field-key="financial_year_end" label="FYE" />?</template>
            <template v-else>Whether the company (or group if consolidated accounts are prepared) has more than 50 employees as at <CddVarField field-key="financial_year_end" label="FYE" />?</template>
          </td>
          <td style="white-space:nowrap">
            <span class="doc-toggle" :class="{ off: yn(q.k) !== 'yes' }" @click="setYn(q.k, 'yes')">{{ yn(q.k) === 'yes' ? '☑' : '☐' }} Yes</span>
            <span class="doc-toggle" :class="{ off: yn(q.k) !== 'no' }" style="margin-left:10px" @click="setYn(q.k, 'no')">{{ yn(q.k) === 'no' ? '☑' : '☐' }} No</span>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Additional info: employees + subsidiary -->
    <div class="sec-title" style="margin-top:14px">Additional information</div>
    <table class="cdd-tbl" style="width:auto">
      <tbody>
        <tr><td class="cdd-lcell">Number of employees of the company at FYE</td><td><CddVarField field-key="number_of_employees_company" label="No. (company)" /></td></tr>
        <tr><td class="cdd-lcell">No. of employees of the Group (if part of a group)</td><td><CddVarField field-key="number_of_employees_group" label="No. (group)" /></td></tr>
      </tbody>
    </table>
    <p class="doc-p">Please tick the appropriate box:</p>
    <p class="doc-p">
      <span v-for="b in V_EMP_BANDS" :key="b.v" class="doc-toggle" :class="{ off: band() !== b.v }" style="margin-right:14px" @click="setBand(b.v)">{{ band() === b.v ? '☑' : '☐' }} {{ b.label }}</span>
    </p>
    <table class="cdd-tbl" style="width:auto">
      <tbody>
        <tr v-for="q in V_YESNO.slice(3)" :key="q.k">
          <td class="cdd-lcell">{{ q.k === 'is_subsidiary' ? 'Whether the company is a subsidiary?' : 'Whether the company has any subsidiary/associate/joint venture?' }}</td>
          <td style="white-space:nowrap">
            <span class="doc-toggle" :class="{ off: yn(q.k) !== 'yes' }" @click="setYn(q.k, 'yes')">{{ yn(q.k) === 'yes' ? '☑' : '☐' }} Yes</span>
            <span class="doc-toggle" :class="{ off: yn(q.k) !== 'no' }" style="margin-left:10px" @click="setYn(q.k, 'no')">{{ yn(q.k) === 'no' ? '☑' : '☐' }} No</span>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Particulars loops -->
    <VerifyLoopBlock v-for="lp in V_LOOPS" :key="lp.arrayKey" :loop="lp" />

    <!-- Confirmation -->
    <p class="doc-p" style="margin-top:16px">I, <CddVarField field-key="confirming_director_name" label="Name of Director" /> hereby confirm the above particulars are true and correct to the best of my knowledge and belief with * no amendment to be made / amendments to be made as indicated above.</p>
    <p class="doc-p">I confirm having read and understood the below <b class="v-red">“Important Notification”</b>.</p>
    <div class="sign-block"><div class="sign-line"><CddVarField field-key="confirming_director_name" label="Name of Director" /><br />Director</div></div>
    <p class="doc-p">Date: <CddVarField field-key="ar_filing_date" label="AR filing date" /></p>
    <p class="doc-note">* delete whichever is inapplicable</p>
  </div>
</template>

<style scoped>
.vdoc :deep(.cdd-tbl) { margin: 6px 0; }
.vhdr { width: auto; margin-bottom: 10px; }
.vlb-t { display: flex; align-items: center; gap: 10px; margin-top: 14px; }
.vlb-add { border: 1px solid #cfe0ff; background: #f4f8ff; color: #1B64DA; font-size: 11px; font-weight: 800; border-radius: 7px; padding: 2px 10px; cursor: pointer; }
.vlb-add:hover { background: #e6f0ff; }
.v-red { color: #e00000; }
.vlb-rm { border: none; background: none; color: #c0392b; font-size: 13px; cursor: pointer; }
</style>
